import React, { useState, useRef, useContext, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, Select, MenuItem,
  InputLabel, FormControl, Paper, Stack, styled,
  IconButton, InputAdornment, CircularProgress, FormHelperText,
} from '@mui/material';
import {
  Landscape,
  Home,
  Storefront,
  House,
  Apartment,
  BusinessCenter,
  Villa,
  HolidayVillage,
  OtherHouses,
  Close as CloseIcon,
  CheckCircle,
  // Highlights icons
  Security,
  FitnessCenter,
  SportsBasketball,
  DirectionsRun,
  ChildCare,
  Park,
  SelfImprovement,
  WorkspacePremium,
  SportsScore,
  Videocam,
  BeachAccess,
  Forest,
  Pool,
  Lock,
  LocationCity,
  DirectionsCar,
  Description,
  // Amenities icons
  Theaters,
  Celebration,
  LocalLibrary,
  LocalCafe,
  Hotel,
  DirectionsBike,
  Spa,
  SportsTennis,
  SportsCricket,
  SportsSoccer,
  SportsVolleyball,
  RollerSkating,
  Doorbell,
  Phone,
  DoorFront,
  People,
  LocalFireDepartment,
  ExitToApp,
  Bolt,
  WaterDrop,
  Business,
  DeleteOutline,
  WbSunny,
  EvStation,
  LocalGasStation,
  Elevator,
  SupportAgent,
  Chair,
  Build,
  CleaningServices,
  Deck,
  ChildFriendly,
  LocalPharmacy,
  LocalAtm,
  ContentCut,
  LocalLaundryService,
  LocalHospital,
} from '@mui/icons-material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { GoogleMap, useJsApiLoader, Marker, Autocomplete } from '@react-google-maps/api';
import SearchBar from './FormsSearchBar';
import FormsBottomNavbar from './FormsBottomNavbar';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from './../../AuthContext/AuthContext';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { BASE_URL, Notification_Url } from './../../Api/ApiUrls';
import { checkPropertyUploadLimit_M } from "./checkPropertyUploadLimit_M";
import SubscriptionModal from "./SubscriptionModal";
import { GOOGLEMAPS_API_KEY } from './../../Api/ApiUrls';
import GuestLoginModal from '../../GuestLoginModal';
import SuccessAlert from '../../../Components/Users/Alertpage/SuccessAlert/SuccessAlert';
import FailureAlert from '../../../Components/Users/Alertpage/FailureAlert/FailureAlert';
import FullScreenLoader from '../../FullScreenLoader';

const GOOGLE_MAPS_API_KEY = GOOGLEMAPS_API_KEY;

const containerStyle = {
  width: '100%',
  height: '300px',
};

const centerDefault = {
  lat: 18.387449,
  lng: 78.803236,
};

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const RedButton = styled(Button)({
  backgroundColor: 'red',
  color: 'white',
  '&:hover': { backgroundColor: '#cc0000' },
});
const GreenButton = styled(Button)({
  backgroundColor: 'green',
  color: 'white',
  '&:hover': { backgroundColor: '#008000' },
});

// ============================================================
// Highlights + Amenities option lists
// ============================================================
const highlightOptions = [
  { label: "24-7 Security", icon: <Security /> },
  { label: "Power Backup", icon: <Security /> },
  { label: "Gym", icon: <FitnessCenter /> },
  { label: "Indoor Games", icon: <SportsBasketball /> },
  { label: "Jogging Track", icon: <DirectionsRun /> },
  { label: "Children Play Area", icon: <ChildCare /> },
  { label: "Senior Citizen Park", icon: <Park /> },
  { label: "Yoga Deck", icon: <SelfImprovement /> },
  { label: "Multipurpose Club Hall", icon: <WorkspacePremium /> },
  { label: "Mini Theatre", icon: <SportsScore /> },
  { label: "Co-working Space", icon: <BusinessCenter /> },
  { label: "EV Charging", icon: <Videocam /> },
  { label: "Visitor Parking", icon: <Storefront /> },
  { label: "Rain Water Harvesting", icon: <BeachAccess /> },
  { label: "Landscape Garden", icon: <Forest /> },
  { label: "CCTV", icon: <Videocam /> },
  { label: "Club House", icon: <Home /> },
  { label: "Swimming Pool", icon: <Pool /> },
  { label: "Gated Community", subtitle: "24/7 Security", icon: <Lock /> },
  { label: "2, 3 & 4 BHK", subtitle: "Configurations", icon: <Apartment /> },
  { label: "High Rise", subtitle: "Premium Living", icon: <LocationCity /> },
  { label: "Covered Parking", subtitle: "1 & 2 Cars", icon: <DirectionsCar /> },
  { label: "Swimming Pool", subtitle: "With Kids Pool", icon: <Pool /> },
  { label: "RERA Registered", subtitle: "Verified Project", icon: <Description /> },
];

const amenitiesOptions = [
  // Recreation
  { label: "Swimming Pool", category: "Recreation", icon: <Pool /> },
  { label: "Kids Pool", category: "Recreation", icon: <Pool /> },
  { label: "Club House", category: "Recreation", icon: <Home /> },
  { label: "Indoor Games", category: "Recreation", icon: <SportsBasketball /> },
  { label: "Mini Theatre", category: "Recreation", icon: <Theaters /> },
  { label: "Party Hall", category: "Recreation", icon: <Celebration /> },
  { label: "Library", category: "Recreation", icon: <LocalLibrary /> },
  { label: "Cafe", category: "Recreation", icon: <LocalCafe /> },
  { label: "Guest Rooms", category: "Recreation", icon: <Hotel /> },
  // Fitness & Wellness
  { label: "Gym", category: "Fitness & Wellness", icon: <FitnessCenter /> },
  { label: "Yoga Room", category: "Fitness & Wellness", icon: <SelfImprovement /> },
  { label: "Jogging Track", category: "Fitness & Wellness", icon: <DirectionsRun /> },
  { label: "Cycling Track", category: "Fitness & Wellness", icon: <DirectionsBike /> },
  { label: "Spa", category: "Fitness & Wellness", icon: <Spa /> },
  { label: "Sauna", category: "Fitness & Wellness", icon: <Spa /> },
  { label: "Steam Room", category: "Fitness & Wellness", icon: <Spa /> },
  { label: "Meditation Area", category: "Fitness & Wellness", icon: <SelfImprovement /> },
  { label: "Fitness Centre", category: "Fitness & Wellness", icon: <FitnessCenter /> },
  // Sports
  { label: "Tennis Court", category: "Sports", icon: <SportsTennis /> },
  { label: "Basketball Court", category: "Sports", icon: <SportsBasketball /> },
  { label: "Badminton Court", category: "Sports", icon: <SportsTennis /> },
  { label: "Cricket Net", category: "Sports", icon: <SportsCricket /> },
  { label: "Football Field", category: "Sports", icon: <SportsSoccer /> },
  { label: "Table Tennis", category: "Sports", icon: <SportsTennis /> },
  { label: "Volleyball Court", category: "Sports", icon: <SportsVolleyball /> },
  { label: "Skating Area", category: "Sports", icon: <RollerSkating /> },
  { label: "Kids Play Area", category: "Sports", icon: <ChildCare /> },
  // Security
  { label: "24x7 Security", category: "Security", icon: <Security /> },
  { label: "CCTV Surveillance", category: "Security", icon: <Videocam /> },
  { label: "Video Door Phone", category: "Security", icon: <Doorbell /> },
  { label: "Intercom", category: "Security", icon: <Phone /> },
  { label: "Gated Entry", category: "Security", icon: <DoorFront /> },
  { label: "Visitor Mgmt", category: "Security", icon: <People /> },
  { label: "Fire Safety", category: "Security", icon: <LocalFireDepartment /> },
  { label: "Access Control", category: "Security", icon: <Lock /> },
  { label: "Emergency Exit", category: "Security", icon: <ExitToApp /> },
  // Utilities
  { label: "Power Backup", category: "Utilities", icon: <Bolt /> },
  { label: "Water Supply", category: "Utilities", icon: <WaterDrop /> },
  { label: "Rainwater Harvesting", category: "Utilities", icon: <WaterDrop /> },
  { label: "Sewage Treatment", category: "Utilities", icon: <Business /> },
  { label: "Waste Mgmt", category: "Utilities", icon: <DeleteOutline /> },
  { label: "Solar Power", category: "Utilities", icon: <WbSunny /> },
  { label: "EV Charging", category: "Utilities", icon: <EvStation /> },
  { label: "Gas Pipeline", category: "Utilities", icon: <LocalGasStation /> },
  { label: "STP / WTP", category: "Utilities", icon: <Business /> },
  // Building Facilities
  { label: "High Speed Lift", category: "Building Facilities", icon: <Elevator /> },
  { label: "Lobby", category: "Building Facilities", icon: <Business /> },
  { label: "Reception", category: "Building Facilities", icon: <SupportAgent /> },
  { label: "Waiting Area", category: "Building Facilities", icon: <Chair /> },
  { label: "Basement Parking", category: "Building Facilities", icon: <DirectionsCar /> },
  { label: "Maintenance", category: "Building Facilities", icon: <Build /> },
  { label: "Housekeeping", category: "Building Facilities", icon: <CleaningServices /> },
  { label: "Service Lift", category: "Building Facilities", icon: <Elevator /> },
  { label: "Common Terrace", category: "Building Facilities", icon: <Deck /> },
  // Family & Convenience
  { label: "Kids Play Area", category: "Family & Convenience", icon: <ChildCare /> },
  { label: "Creche/Day Care", category: "Family & Convenience", icon: <ChildFriendly /> },
  { label: "Senior Citizen Area", category: "Family & Convenience", icon: <Park /> },
  { label: "Convenience Store", category: "Family & Convenience", icon: <Storefront /> },
  { label: "Pharmacy", category: "Family & Convenience", icon: <LocalPharmacy /> },
  { label: "ATM", category: "Family & Convenience", icon: <LocalAtm /> },
  { label: "Salon", category: "Family & Convenience", icon: <ContentCut /> },
  { label: "Laundry", category: "Family & Convenience", icon: <LocalLaundryService /> },
  { label: "Medical Centre", category: "Family & Convenience", icon: <LocalHospital /> },
];

const amenityCategories = [
  "All",
  "Recreation",
  "Fitness & Wellness",
  "Sports",
  "Security",
  "Utilities",
  "Building Facilities",
  "Family & Convenience",
];

const getHighlightKey = (item) => `${item.label}|${item.subtitle || ""}`;
const getAmenityKey = (item) => `${item.category}|${item.label}`;

const ICON_KEYS = {
  "Swimming Pool": "pool",
  Gym: "gym",
  "Gated Community": "gate",
  "Near Metro": "metro",
};

const slugify = (s) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const getIconKey = (label) => ICON_KEYS[label] || slugify(label);

// ============================================================
// Field maps (unchanged from original)
// ============================================================
const fieldMap = {
  '1BHK': ['Facing', 'Rent Per Month', 'Deposit Amount Per Year', 'No.of Cars Parking', 'Area', 'Total Balconies', 'Total Toilets'],
  '2BHK': ['Facing', 'Rent Per Month', 'Deposit Amount Per Year', 'No.of Cars Parking', 'Area', 'Total Balconies', 'Total Toilets'],
  '3BHK': ['Facing', 'Rent Per Month', 'Deposit Amount Per Year', 'No.of Cars Parking', 'Area', 'Total Balconies', 'Total Toilets'],
  '4+BHK': ['Facing', 'Rent Per Month', 'Deposit Amount Per Year', 'No.of Cars Parking', 'Area', 'Total Balconies', 'Total Toilets'],
  'Plot': ['Facing', 'Rent Per Month', 'Deposit Amount Per Year', 'Area'],
  'Land': ['Facing', 'Rent Per Month', 'Deposit Amount Per Year', 'Acres'],
  'Duplex house': ['Facing', 'Rent Per Month', 'Deposit Amount Per Year', 'No.of Cars Parking', 'Area', 'Total Balconies', 'Total Toilets'],
  'Commercial land': ['Facing', 'Rent Per Month', 'Deposit Amount Per Year', 'Area'],
  'Commercial building/space': ['Facing', 'Rent Per Month', 'Deposit Amount Per Year', 'No.of Cars Parking', 'Area', 'No.of Floors', 'No.of Rooms', 'Total Balconies', 'Total Toilets'],
  'Villa': ['Facing', 'Rent Per Month', 'Deposit Amount Per Year', 'No.of Cars Parking', 'Area', 'Total Balconies', 'Total Toilets'],
  'Pg-school-office': ['Facing', 'Rent Per Month', 'Deposit Amount Per Year', 'No.of Cars Parking', 'Area', 'No.of Floors', 'Rooms Count', 'Total Balconies', 'Total Toilets'],
  'Shopping mall/shop': ['Facing', 'Rent Per Month', 'Deposit Amount Per Year', 'No.of Cars Parking', 'Area', 'No.of Floors', 'Total Balconies', 'Total Toilets'],
};

const areaLabelMap = {
  '1BHK': 'Carpet Area',
  '2BHK': 'Carpet Area',
  '3BHK': 'Carpet Area',
  '4+BHK': 'Carpet Area',
  'Plot': 'Total Area',
  'Duplex house': 'Carpet Area',
  'Commercial land': 'Plot Area',
  'Villa': 'Carpet Area',
  'Pg-school-office': 'Carpet Area',
  'Shopping mall/shop': 'Carpet Area',
};

const subPropertyConfig = {
  'Commercial building/space': [
    'Offices', 'Hospitals', 'Malls', 'Bank', 'Gym', 'Restaurants', 'Hotels', 'IT Parks',
    'Multi Use Spaces', 'Shopping Complexes', 'Retail Showrooms', 'Warehouses',
    'Industrial Units', 'Manufacturing Units', 'Factories', 'Data Centers', 'Co-working Spaces',
  ],
  'Pg-school-office': ['Pg-schools', 'Offices'],
  'Shopping mall/shop': ['Shops', 'Retail Shops'],
};

const countFieldLabels = [
  'No.of Cars Parking',
  'No.of Floors',
  'Rooms Count',
  'No.of Rooms',
  'Total Balconies',
  'Total Toilets',
];

const categoryResetValues = {
  facing: '',
  price: '',
  advance_payment: '',
  parking: '',
  site_area: '',
  no_of_flores: '',
  rooms_count: '',
  balcony: '',
  bathrooms_count: '',
  property_name: '',
  property_type: '',
  highlights: [],
  amenities: [],
};

const facingOptions = ['East', 'West', 'North', 'South', 'North-East', 'North-West', 'South-East', 'South-West'];

const CategoryIconBadge = ({ icon: IconComponent }) => (
  <Box sx={{
    width: 32,
    height: 32,
    borderRadius: '50%',
    backgroundColor: '#F5EAD8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }}>
    <IconComponent sx={{ color: '#8B6914', fontSize: 20 }} />
  </Box>
);

const categoryIcons = {
  '1BHK': <CategoryIconBadge icon={Home} />,
  '2BHK': <CategoryIconBadge icon={Home} />,
  '3BHK': <CategoryIconBadge icon={Home} />,
  '4+BHK': <CategoryIconBadge icon={Home} />,
  'Plot': <CategoryIconBadge icon={Home} />,
  'Land': <CategoryIconBadge icon={Landscape} />,
  'Duplex house': <CategoryIconBadge icon={House} />,
  'Commercial land': <CategoryIconBadge icon={Storefront} />,
  'Commercial building/space': <CategoryIconBadge icon={BusinessCenter} />,
  'Villa': <CategoryIconBadge icon={Villa} />,
  'Pg-school-office': <CategoryIconBadge icon={OtherHouses} />,
  'Shopping mall/shop': <CategoryIconBadge icon={Storefront} />,
};

// ============================================================
// ✅ VALIDATION HELPERS
// ============================================================
// DOM id used to scroll to a field
const fieldId = (key) => `field-${slugify(String(key))}`;

// Which dynamic fields are mandatory (same rules as the old `required` props).
// Optional: all count dropdowns, Area, Acres.
// Required: Facing, Rent Per Month, Deposit Amount Per Year.
const isFieldRequired = (label) => {
  const optional = ['Area', 'Acres', ...countFieldLabels];
  return !optional.includes(label);
};

const LOCATION_ERROR_MSG = 'Please search and confirm your location (tap the ✓ button on the map)';

// ============================================================
// Component
// ============================================================
const RentForm = () => {
  const [workPhotos, setWorkPhotos] = useState([]);
  const { userId, isGuest } = useContext(AuthContext);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [apiHitCount, setApiHitCount] = useState(0);
  const [usingCurrentLocation, setUsingCurrentLocation] = useState(false);
  const [postedBy, setPostedBy] = useState('');
  const [uploadStatus, setUploadStatus] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const mapRef = useRef(null);
  const [postedByOptions, setPostedByOptions] = useState([]);
  const [uploadLimits, setUploadLimits] = useState(null);
  const [locationConfirmed, setLocationConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFailure, setShowFailure] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  // ✅ Validation errors: { [fieldKey]: 'message' }
  const [errors, setErrors] = useState({});

  const clearError = (key) =>
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const { [key]: _removed, ...rest } = prev;
      return rest;
    });

  // Amenity category tab state
  const [selectedAmenityCategory, setSelectedAmenityCategory] = useState("All");

  const [subscriptionModal, setSubscriptionModal] = useState({
    open: false,
    type: null,
  });

  useEffect(() => {
    if (isGuest) {
      setShowGuestModal(true);
    }
  }, [isGuest]);

  useEffect(() => {
    const loadPermissions = async () => {
      const result = await checkPropertyUploadLimit_M(userId);

      if (!result.allowed) {
        setSubscriptionModal({
          open: true,
          type:
            result.reason === "NO_SUBSCRIPTION"
              ? "NO_SUBSCRIPTION"
              : "LIMIT_EXCEEDED",
        });
        return;
      }

      setUploadLimits(result.availability);

      const options = [];
      if (result.availability.Agent?.allowed) options.push("Agent");
      if (result.availability.Owner?.allowed) options.push("Owner");
      if (result.availability.Builder?.allowed) options.push("Builder");

      if (options.length === 0) {
        setSubscriptionModal({ open: true, type: "LIMIT_EXCEEDED" });
        return;
      }

      setPostedByOptions(options);
    };

    if (userId) loadPermissions();
  }, [userId]);

  // Clear image error automatically once an image is added
  useEffect(() => {
    if (workPhotos.length > 0) clearError('images');
  }, [workPhotos.length]);

  const isDisabled = isGuest || (uploadStatus && !uploadStatus.canUpload);

  const generateNumberOptions = (max = 50) => {
    const options = [];
    for (let i = 0; i <= max; i++) options.push(i);
    return options;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === "posted_by") {
      clearError('postedBy');
      setPostedBy(value);
      setFormData((prevData) => ({ ...prevData, posted_by: value }));
    } else if (name === "description") {
      setFormData((prevData) => ({ ...prevData, description: value }));
      setFormValues((prev) => ({ ...prev, description: value }));
    } else {
      setFormData((prevData) => ({ ...prevData, [name]: value }));
      setFormValues((prev) => ({ ...prev, [name]: value }));
    }
  };

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: ['places'],
  });

  const [markerIcon, setMarkerIcon] = useState(null);

  useEffect(() => {
    if (isLoaded && window.google) {
      setMarkerIcon({
        url: "data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%3E%3Cpath%20fill%3D%22%23ff0000%22%20d%3D%22M12%202C8.13%202%205%205.13%205%209c0%205.25%207%2013%207%2013s7-7.75%207-13c0-3.87-3.13-7-7-7zm0%209.5c-1.38%200-2.5-1.12-2.5-2.5s1.12-2.5%202.5-2.5%202.5%201.12%202.5%202.5-1.12%202.5-2.5%202.5z%22%2F%3E%3C%2Fsvg%3E",
        scaledSize: new window.google.maps.Size(40, 40),
        origin: new window.google.maps.Point(0, 0),
        anchor: new window.google.maps.Point(20, 40)
      });
    }
  }, [isLoaded]);

  // ============================================================
  // Image handlers — preview grid, cap at 4
  // ============================================================
  const handleWorkPhotosChange = (e) => {
    if (!e.target.files) return;

    const newFiles = Array.from(e.target.files).filter((file) =>
      file.type.startsWith("image/")
    );

    if (newFiles.length !== e.target.files.length) {
      alert("Please select image files only");
      e.target.value = "";
      return;
    }

    setWorkPhotos((prev) => {
      const merged = [...prev, ...newFiles];
      if (merged.length > 4) {
        alert("❌ You can upload maximum 4 images only");
        return merged.slice(0, 4);
      }
      return merged;
    });

    e.target.value = "";
  };

  const handleRemovePhoto = (index) => {
    setWorkPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const [formData, setFormData] = useState({
    user_id: userId,
    category_id: '',
    facing: '',
    mobile_no: '',
    roadwidth: '',
    site_area: '',
    buildup_area: '',
    posted_by: '',
    price: '',
    location: '',
    lat: '',
    long: '',
    nearby: '',
    no_of_flores: '',
    _1bhk_count: '',
    bedrooms_count: '',
    balcony: '',
    bathrooms_count: '',
    rooms_count: '',
    gated_security: '',
    parking: '',
    advance_payment: '',
    property_type: '',
    description: '',
    max_acres: '',
    units: 'sq.ft',
    highlights: [],
    amenities: [],
    landmark: '',
  });
  const [location, setLocation] = useState(centerDefault);
  const [address, setAddress] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [subPropertyType, setSubPropertyType] = useState('');
  const [formValues, setFormValues] = useState({});
  const autocompleteRef = useRef(null);
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);

  // Clear location error once an address exists AND the pin is confirmed
  useEffect(() => {
    if (address && locationConfirmed) clearError('location');
  }, [address, locationConfirmed]);

  useEffect(() => {
    const fetchMobileNo = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/users/${userId}/`);
        setFormData(prev => ({
          ...prev,
          mobile_no: response.data.mobile_no || "",
        }));
      } catch (error) {
        console.error("Error fetching mobile number:", error);
      }
    };

    if (userId) fetchMobileNo();
  }, [userId]);

  const incrementApiHit = () => {
    setApiHitCount(prev => prev + 1);
    console.log(`Google Maps API hits: ${apiHitCount + 1}`);
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      setUsingCurrentLocation(true);
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setLocation(pos);

          try {
            const response = await fetch(
              `https://maps.googleapis.com/maps/api/geocode/json?latlng=${pos.lat},${pos.lng}&key=${GOOGLE_MAPS_API_KEY}`
            );
            const data = await response.json();

            if (data.status == 'OK' && data.results.length > 0) {
              setAddress(data.results[0].formatted_address);
            } else {
              setAddress(`Lat: ${pos.lat.toFixed(6)}, Lng: ${pos.lng.toFixed(6)}`);
            }
          } catch (error) {
            console.error('Google Maps API error:', error);
            setAddress(`Lat: ${pos.lat.toFixed(6)}, Lng: ${pos.lng.toFixed(6)}`);
          }
        },
        (error) => {
          console.error("Error getting current location:", error);
          alert("Error getting current location. Please enable location services.");
        }
      );
    } else {
      alert("Geolocation is not supported by this browser.");
    }
  };

  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place && place.geometry) {
        incrementApiHit();
        setUsingCurrentLocation(false);
        const newLoc = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };
        setLocation(newLoc);
        setAddress(place.formatted_address);
      }
    }
  };

  const handleMapClick = async (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setLocation({ lat, lng });
    setUsingCurrentLocation(false);
    incrementApiHit();

    if (window.google && window.google.maps) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status == 'OK' && results[0]) {
          setAddress(results[0].formatted_address);
        } else {
          setAddress(`Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`);
        }
      });
    }
  };

  useEffect(() => {
    axios.get(`${BASE_URL}/property-category/`)
      .then(response => {
        const rentCategories = response.data.filter(
          cat => cat.category_type.toLowerCase() == 'rent/lease'
        ); 
        const customOrder = [
          "1BHK", "2BHK", "3BHK", "4+BHK",
          "Plot", "Land", "Duplex house", "Commercial land",
          "Commercial building/space", "Villa",
          "Pg-school-office", "Shopping mall/shop"
        ];

        const sortedCategories = rentCategories.sort((a, b) => {
          return customOrder.indexOf(a.category) - customOrder.indexOf(b.category);
        });

        setCategories(sortedCategories);
      })
      .catch(error => {
        console.error("Error fetching categories:", error);
      });
  }, []);

  const groupedCategories = categories.reduce((acc, curr) => {
    const type = curr.category_type;
    if (!acc[type]) acc[type] = [];
    acc[type].push(curr);
    return acc;
  }, {});

  const geocodeAddress = async () => {
    if (!address) return;
    if (usingCurrentLocation) return;
    if (window.google && window.google.maps) {
      incrementApiHit();
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address }, (results, status) => {
        if (status == 'OK' && results[0]) {
          const location = results[0].geometry.location;
          setLocation({ lat: location.lat(), lng: location.lng() });
          setAddress(results[0].formatted_address);
        }
      });
    }
  };

  const handleBackClick = () => navigate(-1);

  const labelKeyMap = {
    'Facing': 'facing',
    'Rent Per Month': 'price',
    'Deposit Amount Per Year': 'advance_payment',
    'No.of Cars Parking': 'parking',
    'Area': 'site_area',
    'Acres': 'site_area',
    'No.of Floors': 'no_of_flores',
    'Rooms Count': 'rooms_count',
    'No.of Rooms': 'rooms_count',
    'Total Balconies': 'balcony',
    'Total Toilets': 'bathrooms_count',
  };

  const isNumericField = (label) => {
    const numericFields = [
      'Rent Per Month',
      'Deposit Amount Per Year',
      'Area',
      'Acres',
    ];
    return numericFields.includes(label);
  };

  const isValidNumber = (value) => {
    return value === '' || /^[0-9]*\.?[0-9]*$/.test(value);
  };

  const handleFieldChange = (label, value) => {
    clearError(label);

    const key = labelKeyMap[label] || label;

    if (isNumericField(label) && !isValidNumber(value)) return;

    setFormValues(prev => ({ ...prev, [label]: value }));
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleSearchClick = () => console.log('Search icon clicked');
  const handleFilterClick = () => console.log('Filter icon clicked');

  // ============================================================
  // Highlights / Amenities toggles
  // ============================================================
  const toggleHighlight = (key) => {
    setFormData((prev) => {
      const isSelected = prev.highlights.includes(key);
      const updatedHighlights = isSelected
        ? prev.highlights.filter((h) => h !== key)
        : [...prev.highlights, key];
      return { ...prev, highlights: updatedHighlights };
    });
  };

  const toggleAmenity = (key) => {
    setFormData((prev) => {
      const isSelected = prev.amenities.includes(key);
      const updatedAmenities = isSelected
        ? prev.amenities.filter((a) => a !== key)
        : [...prev.amenities, key];
      return { ...prev, amenities: updatedAmenities };
    });
  };

  const subPropertyOptions = subPropertyConfig[selectedCategory] || [];

  if (!isLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>Loading Google Maps...</Typography>
      </Box>
    );
  }

  // ============================================================
  // ✅ FORM VALIDATION
  // Errors are inserted in on-screen order, so the first key is
  // always the top-most invalid field.
  // ============================================================
  const validateForm = () => {
    const errs = {};
    const fieldList = fieldMap[selectedCategory] || [];

    if (!selectedCategory) errs.category = 'Please select a property type';

    if (subPropertyOptions.length > 0 && !subPropertyType) {
      errs.subProperty = 'Please select a sub property type';
    }

    // Dynamic fields (Facing, Rent Per Month, Deposit Amount Per Year)
    fieldList.forEach((label) => {
      if (!isFieldRequired(label)) return;
      const val = formValues[label];
      if (val === undefined || val === null || String(val).trim() === '') {
        errs[label] = `${label} is required`;
      }
    });

    // Location (remove the `locationConfirmed` check if you don't want to force the pin confirm)
    if (!address.trim() || !locationConfirmed) {
      errs.location = LOCATION_ERROR_MSG;
    }

    // Images
    if (workPhotos.length === 0) errs.images = 'Please upload at least 1 image';

    // Posted by
    if (!postedBy) errs.postedBy = 'Please select Posted By';

    return errs;
  };

  const scrollToFirstError = (errs) => {
    const firstKey = Object.keys(errs)[0];
    if (!firstKey) return;
    const el = document.getElementById(fieldId(firstKey));
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => {
      el.querySelector('input, textarea, [role="combobox"]')?.focus({ preventScroll: true });
    }, 400);
  };

  // ============================================================
  // Submit
  // ============================================================
  const handleSubmit = async (e) => {
    e.preventDefault();

    // ✅ Run validation, show red messages and scroll to the first invalid field
    const validationErrors = validateForm();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      scrollToFirstError(validationErrors);
      return;
    }

    if (workPhotos.length > 4) {
      alert("❌ You can upload maximum 4 images only");
      return;
    }

    if (!uploadLimits) {
      setSubscriptionModal({ open: true, type: "NO_SUBSCRIPTION" });
      return;
    }

    const roleKey =
      postedBy === "Agent"
        ? "Agent"
        : postedBy === "Builder"
          ? "Builder"
          : "Owner";

    if (!uploadLimits[roleKey]?.allowed) {
      setSubscriptionModal({ open: true, type: "LIMIT_EXCEEDED" });
      return;
    }

    setLoading(true);

    try {
      let site_area = parseFloat(formData.site_area) || 0;

      const updatedFormData = {
        ...formData,
        lat: location.lat,
        long: location.lng,
        location: address,
        site_area: site_area.toString(),
        type: "rent",
        property_type: subPropertyOptions.length > 0 ? subPropertyType : '',
      };

      const formDataToSend = new FormData();

      // Append all form fields EXCEPT highlights & amenities
      Object.entries(updatedFormData).forEach(([key, value]) => {
        if (key === 'highlights' || key === 'amenities') return;
        formDataToSend.append(key, value);
      });

      // ✅ Property Highlights payload
      const highlightPayload = formData.highlights
        .map((key) => highlightOptions.find((o) => getHighlightKey(o) === key))
        .filter(Boolean)
        .map((item, index) => ({
          name: item.label,
          icon: getIconKey(item.label),
          order: index + 1,
          is_active: true,
        }));
      formDataToSend.append("property_highlights", JSON.stringify(highlightPayload));
      formDataToSend.append("highlights", JSON.stringify(highlightPayload));

      // ✅ Property Amenities payload (deduped by name)
      const seenAmenities = new Set();
      const amenityPayload = formData.amenities
        .map((key) => amenitiesOptions.find((o) => getAmenityKey(o) === key))
        .filter(
          (item) =>
            item && !seenAmenities.has(item.label) && seenAmenities.add(item.label)
        )
        .map((item, index) => ({
          name: item.label,
          icon: getIconKey(item.label),
          order: index + 1,
          is_active: true,
        }));
      formDataToSend.append("property_amenities", JSON.stringify(amenityPayload));
      formDataToSend.append("amenities", JSON.stringify(amenityPayload));

      workPhotos.forEach((file) => {
        formDataToSend.append("new_property_images", file);
      });

      const response = await axios.post(
        `${BASE_URL}/property/`,
        formDataToSend,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.status === 201 || response.status === 200) {
        setAlertMessage("Property submitted successfully!");
        setShowSuccess(true);
        const usersResponse = await axios.get(`${BASE_URL}/users/`);
        const usersWithToken = (usersResponse.data.data || usersResponse.data)
          .filter((user) => user.fcm_token)
          .map((user) => user.fcm_token);
      } else {
        setAlertMessage("Something went wrong. Try again.");
        setShowFailure(true);
      }
    } catch (error) {
      console.error("Submission Error:", error);

      if (error.response) {
        setAlertMessage(
          `Error: ${error.response.data.message || "Something went wrong"}`
        );
      } else {
        setAlertMessage("An error occurred while submitting. Please try again.");
      }
      setShowFailure(true);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // Highlights + Amenities section renderer
  // ============================================================
  const renderHighlightsAndAmenities = () => (
    <Box sx={{ mt: 3, mb: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: '#3D2B1F' }}>
        Property Highlights
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '8px',
          mb: 4,
        }}
      >
        {highlightOptions.map((item) => {
          const itemKey = getHighlightKey(item);
          const isSelected = formData.highlights.includes(itemKey);
          return (
            <Box
              key={itemKey}
              onClick={() => toggleHighlight(itemKey)}
              sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                gap: 0.5,
                p: 1,
                borderRadius: '10px',
                border: `1px solid ${isSelected ? '#9a4f20' : '#E0E0E0'}`,
                backgroundColor: isSelected ? '#F5EDE4' : '#FFFFFF',
                cursor: 'pointer',
                transition: 'all 0.2s',
                minHeight: '90px',
                justifyContent: 'center',
                overflow: 'hidden',
                '&:hover': {
                  backgroundColor: isSelected ? '#EDE0D4' : '#FAFAFA',
                  borderColor: isSelected ? '#9a4f20' : '#B0B0B0',
                },
              }}
            >
              {isSelected && (
                <CheckCircle
                  sx={{ position: 'absolute', top: 4, right: 4, fontSize: '14px', color: '#9a4f20' }}
                />
              )}
              <Box sx={{ color: '#9a4f20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', mb: 0.5 }}>
                {item.icon}
              </Box>
              <Typography sx={{ fontSize: '10.5px', fontWeight: 600, color: '#1A1A1A', lineHeight: 1.2, wordBreak: 'break-word' }}>
                {item.label}
              </Typography>
              {item.subtitle && (
                <Typography sx={{ fontSize: '9px', fontWeight: 400, color: '#888', lineHeight: 1.2, wordBreak: 'break-word' }}>
                  {item.subtitle}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>

      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5, color: '#3D2B1F' }}>
        Amenities
      </Typography>

      {/* Category Tabs */}
      <Box
        sx={{
          mb: 2,
          overflowX: 'auto',
          whiteSpace: 'nowrap',
          borderBottom: '1px solid #E0E0E0',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        <Stack direction="row" spacing={0} sx={{ display: 'inline-flex' }}>
          {amenityCategories.map((cat) => (
            <Box
              key={cat}
              onClick={() => setSelectedAmenityCategory(cat)}
              sx={{
                px: 1.5,
                py: 1,
                cursor: 'pointer',
                borderBottom:
                  selectedAmenityCategory === cat
                    ? '2px solid #9a4f20'
                    : '2px solid transparent',
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: selectedAmenityCategory === cat ? 600 : 500,
                  color: selectedAmenityCategory === cat ? '#9a4f20' : '#666',
                  whiteSpace: 'nowrap',
                }}
              >
                {cat}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>

      {/* Amenities Grid */}
      {(selectedAmenityCategory === 'All'
        ? amenityCategories.filter((c) => c !== 'All')
        : [selectedAmenityCategory]
      ).map((cat) => {
        const items = amenitiesOptions.filter((a) => a.category === cat);
        if (items.length === 0) return null;

        return (
          <Box key={cat} sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#333', mb: 1.5 }}>
              {cat}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '8px',
              }}
            >
              {items.map((item) => {
                const itemKey = getAmenityKey(item);
                const isSelected = formData.amenities.includes(itemKey);
                return (
                  <Box
                    key={itemKey}
                    onClick={() => toggleAmenity(itemKey)}
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      gap: 0.5,
                      p: 1,
                      borderRadius: '10px',
                      border: `1px solid ${isSelected ? '#9a4f20' : '#E0E0E0'}`,
                      backgroundColor: isSelected ? '#F5EDE4' : '#FFFFFF',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      minHeight: '74px',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      '&:hover': {
                        backgroundColor: isSelected ? '#EDE0D4' : '#FAFAFA',
                        borderColor: isSelected ? '#9a4f20' : '#B0B0B0',
                      },
                    }}
                  >
                    {isSelected && (
                      <CheckCircle
                        sx={{ position: 'absolute', top: 4, right: 4, fontSize: '13px', color: '#9a4f20' }}
                      />
                    )}
                    <Box sx={{ color: '#9a4f20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>
                      {item.icon}
                    </Box>
                    <Typography sx={{ fontSize: '9.5px', fontWeight: 600, color: '#1A1A1A', lineHeight: 1.2, wordBreak: 'break-word' }}>
                      {item.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Box>
  );

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          top: 0, left: 0, right: 0,
          zIndex: 1200,
          backgroundColor: "#fff",
          boxShadow: 1,
        }}
      >
        <SearchBar
          onBackClick={handleBackClick}
          onSearchClick={handleSearchClick}
          onFilterClick={handleFilterClick}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          pt: { xs: '60px', sm: '100px' },
          backgroundColor: 'rgb(239, 231, 221)',
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            p: { xs: 2, sm: 3 },
            pb: 10,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Paper
            elevation={2}
            sx={{
              p: { xs: 2, sm: 3 },
              mb: 3,
              width: '100%',
              boxSizing: 'border-box',
            }}
            component="form"
            noValidate
            onSubmit={handleSubmit}
          >
            <Typography
              variant="h5"
              gutterBottom
              sx={{ fontWeight: 'bold', mb: 3, textAlign: 'center' }}
            >
              Rent Property Form
            </Typography>

            {/* Property Type */}
            <FormControl
              fullWidth
              sx={{ mb: 3 }}
              id={fieldId('category')}
              error={!!errors.category}
            >
              <InputLabel
                id="category-label"
                sx={{ color: '#8B6914', '&.Mui-focused': { color: '#8B6914' } }}
              >
                Select Property Type
              </InputLabel>

              <Select
                labelId="category-label"
                value={selectedCategory}
                label="Select Property Type"
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#8B6914' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#6B4F12' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#8B6914', borderWidth: '2px' },
                  '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#d32f2f' },
                  '& .MuiSelect-select': { display: 'flex', alignItems: 'center', gap: 1 },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 440,
                      '& .MuiMenuItem-root': { display: 'flex', alignItems: 'center', gap: 1.5, padding: '2px 16px', minHeight: '32px' },
                      '& .MuiMenuItem-root.Mui-selected': { backgroundColor: '#F5EDE4', '&:hover': { backgroundColor: '#E8DDD0' } },
                      '& .MuiMenuItem-root:hover': { backgroundColor: '#F5EDE4' },
                    },
                  },
                }}
                onChange={(e) => {
                  const selected = categories.find(
                    cat => cat.category === e.target.value
                  );
                  setSelectedCategory(selected.category);
                  setSubPropertyType('');
                  setFormValues(prev => ({ description: prev.description }));
                  setSelectedAmenityCategory("All");
                  setFormData(prev => ({
                    ...prev,
                    ...categoryResetValues,
                    category_id: selected.category_id,
                    type: selected.category_type,
                    units: selected.category === 'Land' ? 'acre' : 'sq.ft',
                  }));
                  // Old field errors no longer apply after switching category
                  setErrors({});
                }}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {selected && categoryIcons[selected]}
                    <Typography sx={{ color: '#3D2B1F' }}>
                      {selected || 'Select Property Type'}
                    </Typography>
                  </Box>
                )}
              >
                {Object.values(groupedCategories).flat().map(cat => (
                  <MenuItem
                    key={cat.category_id}
                    value={cat.category}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 2, padding: '8px 16px',
                      '&:hover': { backgroundColor: '#F5EDE4 !important' },
                      '&.Mui-selected': { backgroundColor: '#E8DDD0 !important', '&:hover': { backgroundColor: '#E8DDD0 !important' } },
                    }}
                  >
                    <Box sx={{ minWidth: 30 }}>
                      {categoryIcons[cat.category] || <Home sx={{ color: '#8B6914', fontSize: 20 }} />}
                    </Box>
                    <Typography sx={{ color: '#3D2B1F', fontWeight: 500 }}>
                      {cat.category}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
              {errors.category && <FormHelperText>{errors.category}</FormHelperText>}
            </FormControl>

            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Property Nearest Landmark"
                placeholder="Enter nearest landmark"
                variant="outlined"
                value={formData.landmark || ''}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    landmark: e.target.value,
                  }))
                }
              />
            </Box>

            {/* Sub Property Type */}
            {subPropertyOptions.length > 0 && (
              <Box sx={{ mb: 3 }} id={fieldId('subProperty')}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Sub Property Type
                </Typography>
                <FormControl fullWidth error={!!errors.subProperty}>
                  <InputLabel id="sub-property-label">Select Sub Property Type</InputLabel>
                  <Select
                    labelId="sub-property-label"
                    value={subPropertyType}
                    label="Select Sub Property Type"
                    onChange={(e) => {
                      clearError('subProperty');
                      const value = e.target.value;
                      setSubPropertyType(value);
                      setFormData(prev => ({ ...prev, property_type: value }));
                    }}
                    MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                  >
                    {subPropertyOptions.map(opt => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </Select>
                  {errors.subProperty && <FormHelperText>{errors.subProperty}</FormHelperText>}
                </FormControl>
              </Box>
            )}

            {/* Dynamic fields */}
            {selectedCategory && fieldMap[selectedCategory]?.map((label) => {
              const err = errors[label];

              if (label === 'Facing') {
                return (
                  <FormControl
                    fullWidth
                    key={label}
                    id={fieldId(label)}
                    error={!!err}
                    sx={{ mb: 2 }}
                  >
                    <InputLabel>{label}</InputLabel>
                    <Select
                      value={formValues[label] || ''}
                      label={label}
                      onChange={(e) => handleFieldChange(label, e.target.value)}
                    >
                      {facingOptions.map(option => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                    {err && <FormHelperText>{err}</FormHelperText>}
                  </FormControl>
                );
              }

              if (countFieldLabels.includes(label)) {
                return (
                  <FormControl fullWidth key={label} id={fieldId(label)} sx={{ mb: 2 }}>
                    <InputLabel>{label}</InputLabel>
                    <Select
                      value={formValues[label] ?? ''}
                      label={label}
                      onChange={(e) => handleFieldChange(label, e.target.value)}
                      MenuProps={{ PaperProps: { style: { maxHeight: 200 } } }}
                    >
                      {generateNumberOptions().map(num => (
                        <MenuItem key={num} value={num}>{num}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                );
              }

              if (label === 'Area') {
                const areaLabel = areaLabelMap[selectedCategory] || 'Area';
                return (
                  <Box key={label} id={fieldId(label)} sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={2}>
                      <TextField
                        fullWidth
                        label={areaLabel}
                        value={formValues[label] || ''}
                        onChange={(e) => handleFieldChange(label, e.target.value)}
                        type="number"
                      />
                      <TextField
                        select
                        label="Units"
                        sx={{ minWidth: 120 }}
                        value={formData.units || 'sq.ft'}
                        onChange={(e) => setFormData(prev => ({ ...prev, units: e.target.value }))}
                      >
                        {['sq.ft', 'sq.m', 'sq.yd'].map(unit => (
                          <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                        ))}
                      </TextField>
                    </Stack>
                  </Box>
                );
              }

              if (label === 'Acres') {
                return (
                  <Box key={label} id={fieldId(label)} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Land Area</Typography>
                    <Stack direction="row" spacing={2}>
                      <TextField
                        fullWidth
                        label="Acres"
                        value={formValues[label] || ''}
                        onChange={(e) => handleFieldChange(label, e.target.value)}
                        type="number"
                      />
                      <TextField
                        select
                        label="Units"
                        sx={{ minWidth: 120 }}
                        value={formData.units || 'acre'}
                        onChange={(e) => setFormData(prev => ({ ...prev, units: e.target.value }))}
                      >
                        {['acre', 'gunta', 'sq.ft', 'sq.m'].map(unit => (
                          <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                        ))}
                      </TextField>
                    </Stack>
                  </Box>
                );
              }

              // Rent Per Month, Deposit Amount Per Year, etc.
              return (
                <Box key={label} id={fieldId(label)} sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    label={label}
                    variant="outlined"
                    value={formValues[label] || ''}
                    onChange={(e) => handleFieldChange(label, e.target.value)}
                    error={!!err}
                    helperText={err}
                    type={isNumericField(label) ? 'number' : 'text'}
                  />
                </Box>
              );
            })}

            {/* Location Section (search + map) */}
            <Box sx={{ mb: 2 }} id={fieldId('location')}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                Location (Pin point property on map) <span style={{ color: 'red' }}>*</span>
              </Typography>

              <Autocomplete
                onLoad={(ref) => (autocompleteRef.current = ref)}
                onPlaceChanged={onPlaceChanged}
              >
                <TextField
                  fullWidth
                  label="Search Location"
                  variant="outlined"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  onBlur={geocodeAddress}
                  error={!!errors.location}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={getCurrentLocation} edge="end">
                          <MyLocationIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Autocomplete>

              {/* Map with fixed center pin */}
              <div style={{ ...containerStyle, position: 'relative' }}>
                <GoogleMap
                  mapContainerStyle={{ width: '100%', height: '100%' }}
                  center={location}
                  zoom={15}
                  options={{ gestureHandling: 'greedy' }}
                  onLoad={(map) => { mapRef.current = map; }}
                  onDragStart={() => setLocationConfirmed(false)}
                />

                <div
                  style={{
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%, -100%)',
                    pointerEvents: 'none', zIndex: 10,
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24">
                    <path fill="#ff0000" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                  </svg>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    if (window.google && window.google.maps && mapRef.current) {
                      const center = mapRef.current.getCenter();
                      const lat = center.lat();
                      const lng = center.lng();
                      setLocation({ lat, lng });

                      const geocoder = new window.google.maps.Geocoder();
                      geocoder.geocode(
                        { location: { lat, lng } },
                        (results, status) => {
                          if (status === 'OK' && results[0]) {
                            const selectedAddress = results[0].formatted_address;
                            setAddress(selectedAddress);
                            setFormData(prev => ({
                              ...prev,
                              location: selectedAddress,
                              lat: lat.toString(),
                              long: lng.toString(),
                            }));
                          } else {
                            const fallback = `Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`;
                            setAddress(fallback);
                            setFormData(prev => ({
                              ...prev,
                              location: fallback,
                              lat: lat.toString(),
                              long: lng.toString(),
                            }));
                          }
                          setLocationConfirmed(true);
                        }
                      );
                    }
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: locationConfirmed ? 'green' : 'red',
                    border: 'none', borderRadius: '50%',
                    width: '44px', height: '44px', cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                    position: 'absolute', right: '10px', top: '45%',
                    transform: 'translateY(-50%)', zIndex: 10,
                    transition: 'background-color 0.3s ease',
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24">
                    <path fill="white" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                </button>
              </div>

              {errors.location && (
                <Typography
                  variant="caption"
                  sx={{ color: '#d32f2f', display: 'block', mt: 0.5 }}
                >
                  {errors.location}
                </Typography>
              )}
            </Box>

            {/* Image Upload */}
            <Box id={fieldId('images')}>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{
                  fontWeight: 'bold',
                  mt: 3,
                  color: '#3D2B1F',
                }}
              >
                Uploaded Images <span style={{ color: 'red' }}>*</span>
              </Typography>

              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUploadIcon sx={{ color: '#8B6914' }} />}
                fullWidth
                disabled={workPhotos.length >= 4}
                sx={{
                  mb: 1,
                  borderColor: errors.images || workPhotos.length === 0 ? '#d32f2f' : '#8B6914',
                  color: '#8B6914',
                  '&:hover': {
                    borderColor: '#6B4F12',
                    backgroundColor: 'rgba(139, 105, 20, 0.04)',
                  },
                  '& .MuiButton-startIcon': {
                    color: '#8B6914',
                  },
                  '&.Mui-disabled': {
                    borderColor: '#D3C7A8',
                    color: '#B8AC8E',
                  },
                }}
              >
                {workPhotos.length >= 4
                  ? 'Maximum 4 images selected'
                  : 'Upload Images'}

                <VisuallyHiddenInput
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleWorkPhotosChange}
                />
              </Button>

              <Typography
                variant="caption"
                sx={{
                  color: errors.images || workPhotos.length === 0 ? '#d32f2f' : '#8B6914',
                  display: 'block',
                  mb: 1,
                }}
              >
                {errors.images
                  ? errors.images
                  : workPhotos.length === 0
                    ? 'Please upload at least 1 image'
                    : `Max 4 images allowed (${workPhotos.length}/4 selected)`}
              </Typography>
            </Box>

            {workPhotos.length > 0 && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 1,
                  mt: 2,
                  mb: 2,
                }}
              >
                {workPhotos.map((file, index) => {
                  const previewUrl = URL.createObjectURL(file);

                  return (
                    <Box
                      key={`${file.name}-${index}`}
                      sx={{
                        position: 'relative',
                        width: '100%',
                        paddingTop: '100%',
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: '1px solid #E0D5C0',
                        backgroundColor: '#FAF5EF',
                      }}
                    >
                      <Box
                        component="img"
                        src={previewUrl}
                        alt={file.name}
                        onLoad={() => URL.revokeObjectURL(previewUrl)}
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />

                      <IconButton
                        size="small"
                        onClick={() => handleRemovePhoto(index)}
                        sx={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          bgcolor: 'rgba(0,0,0,0.55)',
                          color: 'white',
                          width: 22,
                          height: 22,
                          '&:hover': {
                            bgcolor: 'rgba(0,0,0,0.8)',
                          },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* ============================================================ */}
            {/* Highlights + Amenities                                       */}
            {/* ============================================================ */}
            {renderHighlightsAndAmenities()}

            {/* Posted By */}
            <FormControl
              fullWidth
              margin="normal"
              size="small"
              id={fieldId('postedBy')}
              error={!!errors.postedBy}
              sx={{
                mb: 2,
                '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 },
                '& .MuiSelect-select': { py: 1.5 },
              }}
            >
              <InputLabel id="posted-by-label">Posted By</InputLabel>
              <Select
                labelId="posted-by-label"
                value={postedBy}
                name="posted_by"
                onChange={handleChange}
                MenuProps={{
                  PaperProps: { sx: { maxHeight: 200, width: 'auto', minWidth: 150 } },
                }}
              >
                {postedByOptions.map((role) => (
                  <MenuItem key={role} value={role} sx={{ py: 1 }}>{role}</MenuItem>
                ))}
              </Select>
              {errors.postedBy && <FormHelperText>{errors.postedBy}</FormHelperText>}
            </FormControl>

            {/* Description */}
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>Description</Typography>
            <TextField
              fullWidth
              variant="outlined"
              multiline
              inputProps={{ maxLength: 1500 }}
              rows={4}
              name="description"
              value={formValues.description || ''}
              onChange={handleChange}
              sx={{ mb: 2 }}
            />
            <Typography variant="caption">
              {(formValues.description || '').length}/1500
            </Typography>

            <>
              <FullScreenLoader open={loading} text="Submitting property..." />

              <Stack
                direction="row"
                spacing={2}
                justifyContent="center"
                sx={{ mt: 2, mb: 2 }}
              >
                <RedButton
                  variant="contained"
                  size="large"
                  sx={{ px: 4, fontWeight: "bold" }}
                  onClick={() => navigate(-1)}
                  disabled={loading}
                >
                  Cancel
                </RedButton>

                <GreenButton
                  variant="contained"
                  size="large"
                  type="submit"
                  disabled={loading || isDisabled}
                  sx={{
                    px: 4,
                    fontWeight: "bold",
                    backgroundColor: (loading || isDisabled) ? "#ccc" : undefined,
                    color: (loading || isDisabled) ? "#777" : undefined,
                    cursor: (loading || isDisabled) ? "not-allowed" : "pointer",
                    "&:hover": {
                      backgroundColor: (loading || isDisabled) ? "#ccc" : undefined,
                    },
                  }}
                >
                  {uploadStatus && !uploadStatus.canUpload
                    ? "Upload Limit Reached"
                    : loading
                      ? "Submitting..."
                      : isGuest
                        ? "Login required to submit"
                        : "Submit"}
                </GreenButton>
              </Stack>
            </>
          </Paper>
        </Box>
        <Box sx={{ height: '70px' }} />
      </Box>

      <SubscriptionModal
        open={subscriptionModal.open}
        type={subscriptionModal.type}
        onClose={() => setSubscriptionModal({ open: false, type: null })}
      />
      <GuestLoginModal
        open={showGuestModal}
        onClose={() => { setShowGuestModal(false); }}
      />

      {showSuccess && (
        <SuccessAlert
          message={alertMessage}
          onClose={() => {
            setShowSuccess(false);
            navigate("/post");
          }}
        />
      )}

      {showFailure && (
        <FailureAlert
          message={alertMessage}
          onClose={() => {
            setShowFailure(false);
            setAlertMessage("");
            navigate("/rent-form");
          }}
        />
      )}

      <FormsBottomNavbar />
    </>
  );
};

export default RentForm;